import {
  AbortMultipartDirectUploadOptions,
  CompleteMultipartDirectUploadOptions,
  CreateEntireVideoInstanceArgs,
  EntireVideoSearchOptions,
  GetEntireVideoByIdArgs,
  GetEntireVideoInstanceByIdArgs,
  PreSignMultipartDirectUploadPartsOptions,
} from '@sparemin/api-sdk';
import { createApiAction } from '../utils';
import * as types from './types';

function apiCallAction<M extends types.ServiceMethod, A>(method: M, args: A) {
  return createApiAction(types.ACTION_KEY, method, args);
}

export function uploadVideo(
  video: types.Source | undefined,
  opts: types.UploadVideoOptions,
): types.UploadVideoAction {
  const {
    trimStart,
    trimEnd,
    transcribe,
    language,
    directUploadBucket,
    directUploadKey,
    initiateProcessing,
    providerUserId,
    googleDriveFileId,
    zoomMeetingId,
    zoomRecordingFileId,
    youtubeUrl,
  } = opts;
  return apiCallAction(types.ServiceMethod.UPLOAD_VIDEO, [
    video,
    transcribe,
    trimStart,
    trimEnd,
    language,
    directUploadBucket,
    directUploadKey,
    initiateProcessing,
    providerUserId,
    googleDriveFileId,
    zoomMeetingId,
    zoomRecordingFileId,
    youtubeUrl,
  ] as types.UploadVideoArgs);
}

export function getVideo(id: number | string): types.GetVideoAction {
  return apiCallAction(types.ServiceMethod.GET_VIDEO, [id]);
}

export function getVideos({
  ids,
  includeVariations = false,
}: types.GetVideosOptions): types.GetVideosAction {
  return apiCallAction(types.ServiceMethod.GET_VIDEOS, [
    ids,
    includeVariations,
  ]);
}

export function searchVideo(opts: types.SearchVideoArgs) {
  return apiCallAction(types.ServiceMethod.SEARCH_VIDEO, opts);
}

export function uploadText(
  text: string,
  urlSummaryId?: string,
): types.UploadTextAction {
  return apiCallAction(types.ServiceMethod.UPLOAD_TEXT, [
    text,
    urlSummaryId,
  ] as types.UploadTextArgs);
}

export function getText(id: string): types.GetTextAction {
  return apiCallAction(types.ServiceMethod.GET_TEXT, [id] as [string]);
}

export function convertGifToVideo(
  source: types.Source,
): types.ConvertGifToVideoAction {
  return apiCallAction(types.ServiceMethod.CONVERT_GIF_TO_VIDEO, [source] as [
    types.Source,
  ]);
}

export function parseExternalContent(
  url: string,
): types.ParseExternalContentAction {
  return apiCallAction(types.ServiceMethod.PARSE_EXTERNAL_CONTENT, [url]);
}

export function directUpload(
  file: Blob,
  type: types.DirectUploadFileType = 'audio',
): types.DirectUploadAction {
  return apiCallAction(types.ServiceMethod.DIRECT_UPLOAD, [
    type,
    file.type,
    file instanceof File ? file.name : undefined,
  ]);
}

export function initMultipartDirectUpload(
  file: Blob,
  type: types.DirectUploadFileType = 'audio',
): types.InitMultipartDirectUploadAction {
  return apiCallAction(types.ServiceMethod.MULTIPART_DIRECT_UPLOAD_INIT, [
    type,
    file.type,
    file instanceof File ? file.name : undefined,
  ]);
}

export function presignMultipartDirectUploadParts(
  args: PreSignMultipartDirectUploadPartsOptions,
): types.PresignMultipartDirectUploadPartsAction {
  return apiCallAction(
    types.ServiceMethod.MULTIPART_DIRECT_UPLOAD_PRESIGN,
    args,
  );
}

export function completeMultipartDirectUpload(
  args: CompleteMultipartDirectUploadOptions,
): types.CompleteMultipartDirectUploadAction {
  return apiCallAction(
    types.ServiceMethod.MULTIPART_DIRECT_UPLOAD_COMPLETE,
    args,
  );
}

export function abortMultipartDirectUpload(
  args: AbortMultipartDirectUploadOptions,
): types.AbortMultipartDirectUploadAction {
  return apiCallAction(types.ServiceMethod.MULTIPART_DIRECT_UPLOAD_ABORT, args);
}

export function getMediaContentInfo(
  url: string,
): types.GetMediaContentInfoAction {
  return apiCallAction(types.ServiceMethod.GET_MEDIA_CONTENT_INFO, [url]);
}

export function getMediaSegmentDataByRevisionId({
  uuid,
  revisionId,
}: types.GetMediaSegmentDataByRevisionIdArgs): types.GetMediaSegmentDataByRevisionIdAction {
  return apiCallAction(
    types.ServiceMethod.GET_MEDIA_SEGMENT_INFO_BY_REVISION_ID,
    { uuid, revisionId },
  );
}

export function getVideoLayoutTimelineByRevisionId({
  uuid,
  revisionId,
}: types.GetVideoLayoutTimelineDataByRevisionIdArgs): types.GetVideoLayoutTimelineDataByRevisionIdAction {
  return apiCallAction(
    types.ServiceMethod.GET_VIDEO_LAYOUT_TIMELINE_BY_REVISION_ID,
    { uuid, revisionId },
  );
}

export function updateVideoLayoutTimelineByRevisionId(
  timeline: types.UpdateVideoLayoutTimelineByRevisionIdArgs,
): types.UpdateVideoLayoutTimelineByRevisionIdAction {
  return apiCallAction(
    types.ServiceMethod.UPDATE_VIDEO_LAYOUT_TIMELINE_BY_REVISION_ID,
    timeline,
  );
}

export function updateVideoLayoutTimelineSegment(
  args: types.UpdateVideoLayoutTimelineSegmentArgs,
): types.UpdateVideoLayoutTimelineSegmentAction {
  return apiCallAction(
    types.ServiceMethod.UPDATE_VIDEO_LAYOUT_TIMELINE_SEGMENT,
    args,
  );
}

export function createEntireVideoInstance(
  args: CreateEntireVideoInstanceArgs,
): types.CreateEntireVideoInstanceAction {
  return apiCallAction(types.ServiceMethod.CREATE_ENTIRE_VIDEO_INSTANCE, args);
}

export function getEntireVideoInstanceById(
  args: GetEntireVideoInstanceByIdArgs,
): types.GetEntireVideoInstanceByIdAction {
  return apiCallAction(
    types.ServiceMethod.GET_ENTIRE_VIDEO_INSTANCE_BY_ID,
    args,
  );
}

export function getEntireVideoById(
  args: GetEntireVideoByIdArgs,
): types.GetEntireVideoByIdAction {
  return apiCallAction(types.ServiceMethod.GET_ENTIRE_VIDEO_BY_ID, args);
}

export function searchEntireVideo(
  args: EntireVideoSearchOptions,
): types.SearchEntireVideoAction {
  return apiCallAction(types.ServiceMethod.SEARCH_ENTIRE_VIDEO, args);
}
